/**
 * CSS custom properties derived from brainTokens — the single runtime source
 * for both MUI sx and leftover kt / --kt CSS. Apply once at app boot
 * (main.tsx) so every surface reads the same palette.
 */
import { brainTokens } from "./brainTheme";

const c = brainTokens.color;

/** Flat map of CSS var name → value. Apply to documentElement. */
export const brainCssVars: Record<string, string> = {
  // fonts
  "--font-sans": brainTokens.font.sans,
  "--font-mono": brainTokens.font.mono,

  // surfaces
  "--kt-canvas": c.canvas,
  "--kt-surface": c.pageBg,
  "--kt-surface-muted": c.panelBg,
  "--kt-sidebar-bg": c.sidebarBg,
  "--kt-strip-bg": c.panelBg2,
  "--kt-topbar-bg": c.topBar,

  // borders
  "--kt-border": c.border,
  "--kt-border-strong": c.dropBorder,
  "--kt-row-divider": c.borderLight2,
  "--kt-track": c.borderLight,
  "--kt-chart-grey": c.muted2,
  "--kt-chart-track": c.borderLight3,

  // ink / text
  "--kt-ink": c.ink,
  "--kt-ink-strong": c.ink2,
  "--kt-text-secondary": c.ink4,
  "--kt-text-tertiary": c.muted,
  "--kt-text-muted": c.faint,

  // brand teal
  "--kt-teal": c.teal,
  "--kt-teal-ink": c.tealInk,
  "--kt-teal-tint": c.tealTint,
  "--kt-teal-shadow": "0 1px 3px rgba(15, 143, 143, 0.35)",
  "--kt-on-teal": c.white,

  // logo / topbar (keep brand-dot accents from tokens)
  "--kt-logo-bright": c.brandTeal,
  "--kt-on-logo-bright": "#06302d",
  "--kt-logo-orange": c.brandOrange,
  "--kt-logo-wordmark": "#eaecee",
  "--kt-logo-brain-text": c.brandTeal,
  "--kt-logo-brain-border": "#1c5a55",
  "--kt-topbar-icon": "#c2c9cd",
  "--kt-topbar-pill-border": "#2a3338",
  "--kt-topbar-pill-text": "#9aa3a8",
  "--kt-topbar-kbd": "#5d676d",

  // status
  "--kt-status-active": c.successDot,
  "--kt-status-onboarding": c.teal,
  "--kt-status-paused": c.warnDot,
  "--kt-spine-red": c.dangerText2,
  "--kt-spine-green": c.successDot,
  "--kt-spine-teal": c.teal,
  "--kt-danger-ink": c.dangerText,
  "--kt-danger-border": c.dangerBorder,

  // legacy ab-* aliases (components still reference these)
  "--color-background-tertiary": c.canvas,
  "--color-background-primary": c.pageBg,
  "--color-background-secondary": c.panelBg2,
  "--color-background-hover": c.tealTint,
  "--color-text-primary": c.ink,
  "--color-text-secondary": c.ink4,
  "--color-text-tertiary": c.muted,
  "--color-border-tertiary": c.border,
  "--color-border-secondary": c.dropBorder,
  "--color-primary": c.teal,
  "--color-primary-hover": c.tealHover,
  "--color-on-primary": c.white,
  "--color-text-info": c.teal,
  "--color-background-info": c.tealTint,
  "--color-text-success": c.successText,
  "--color-background-success": c.successBg,
  "--color-text-warning": c.warnText,
  "--color-background-warning": c.warnBg,
  "--color-text-danger": c.dangerText,
  "--color-background-danger": c.dangerBg,

  // radii (align with brainTokens)
  "--border-radius-sm": `${brainTokens.radius.chip}px`,
  "--border-radius-md": `${brainTokens.radius.card}px`,
  "--border-radius-lg": `${brainTokens.radius.panel}px`,
  "--border-radius-xl": `${brainTokens.radius.widget}px`,
  "--border-radius-pill": `${brainTokens.radius.pill}px`,

  // elevation
  "--shadow-card": brainTokens.shadow.card,
  "--shadow-raised": "0 4px 14px rgba(16, 24, 40, 0.08)",
  "--shadow-overlay": "0 16px 40px rgba(16, 24, 40, 0.18)",
  "--focus-ring": "0 0 0 3px rgba(15, 143, 143, 0.18)",
};

/** Apply brainCssVars to :root. Idempotent; call once at boot. */
export function applyBrainCssVars(target: HTMLElement = document.documentElement): void {
  for (const [name, value] of Object.entries(brainCssVars)) {
    target.style.setProperty(name, value);
  }
}
